import { useLocation, Link } from "react-router-dom";
import { useRef, useState } from "react";
import { 
  ChevronRight,
  ChevronDown,
  Building2,
  LogOut,
  UserCog
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useUserCapabilities } from "@/hooks/useCapabilities";
import { useTenantCapabilities } from "@/hooks/useTenantCapabilities";
import { getModuleForPath } from "@/lib/moduleRoutes";
import { isVehicleMasterEnabled } from "@/lib/vehicleMaster";
import { adminNavigationItems, getNavigationConfig, NavigationEntry, NavigationItemGroup, NavigationItem, AdminNavigationEntry } from "@/config/navigationConfig";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";


export function AppSidebar() {
  // All hooks must be called at the top level
  const { open, openMobile, isMobile, setOpen } = useSidebar();
  const location = useLocation();
  const { currentTenant, loading: tenantLoading } = useTenant();
  const { user, loading: authLoading, signOut } = useAuth();
  const { toast } = useToast();
  const userCapabilitiesResult = useUserCapabilities();
  // V3: frontend-only module visibility. Fail-open — navigation is preserved
  // while entitlements load or fail, and hidden only for an explicit disable.
  const { capabilities: moduleCapabilities, isLoading: modulesLoading } = useTenantCapabilities();
  const capabilities = userCapabilitiesResult?.data || [];
  const capabilitiesLoading = userCapabilitiesResult?.isLoading ?? true;
  const currentPath = location.pathname;
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const shouldShowTextEarly = open || openMobile;

  const handleSignOutEarly = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const renderFallbackFooter = () => (
    <SidebarFooter className="border-t border-sidebar-border p-4">
      <div className="space-y-2">
        {shouldShowTextEarly && user?.email && (
          <div className="flex items-center gap-3 text-sm text-sidebar-foreground/80">
            <div className="w-6 h-6 rounded-full bg-sidebar-accent flex items-center justify-center text-xs font-medium">
              {user.email.charAt(0).toUpperCase()}
            </div>
            <span className="truncate flex-1">{user.email}</span>
          </div>
        )}

        {user && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="w-full justify-start text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <Link to="/account">
              <UserCog className="h-4 w-4 flex-shrink-0" />
              {shouldShowTextEarly && <span className="ml-2">Account settings</span>}
            </Link>
          </Button>
        )}

        {user && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOutEarly}
            className="w-full justify-start text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            {shouldShowTextEarly && <span className="ml-2">Sign Out</span>}
          </Button>
        )}
      </div>
    </SidebarFooter>
  );

  // Show loading if any data is still loading
  if (authLoading || tenantLoading || capabilitiesLoading) {
    return (
      <Sidebar collapsible="icon" className="border-r border-sidebar-border print:hidden">
        <SidebarContent>
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </SidebarContent>
        {user && renderFallbackFooter()}
      </Sidebar>
    );
  }

  // Return minimal sidebar if no tenant or user
  if (!currentTenant || !user) {
    return (
      <Sidebar collapsible="icon" className="border-r border-sidebar-border print:hidden">
        <SidebarContent>
          <div className="p-4 text-center text-sm text-muted-foreground">
            No access
          </div>
        </SidebarContent>
        {renderFallbackFooter()}
      </Sidebar>
    );
  }

  // If capabilities loaded but empty, show no permissions message
  if (!capabilitiesLoading && capabilities.length === 0) {
    return (
      <Sidebar collapsible="icon" className="border-r border-sidebar-border print:hidden">
        <SidebarContent>
          <div className="p-4 text-center text-sm text-muted-foreground">
            No permissions configured. Please contact your administrator.
          </div>
        </SidebarContent>
        {renderFallbackFooter()}
      </Sidebar>
    );
  }

  // Helper function to check capabilities
  const hasCap = (capability: string) => {
    return capabilities.some(cap => cap.key === capability && cap.value);
  };

  // Type guard for grouped items
  const isGroupedItem = (item: NavigationEntry): item is NavigationItemGroup => {
    return 'children' in item;
  };

  // Check capabilities using the helper
  const canManageSettings = hasCap('settings.manage');
  const canViewTasks = hasCap('tasks.view_all') || hasCap('tasks.create') || hasCap('tasks.view_own');
  const canViewReports = hasCap('reports.view');
  const canManageDrivers = hasCap('users.manage') || hasCap('teams.manage');
  
  const navigationConfig = getNavigationConfig(currentTenant?.business_type);
  
  // Check if a single item has access
  const hasItemAccess = (item: NavigationItem): boolean => {
    switch (item.url) {
      case '/dashboard':
        return canViewTasks || canManageDrivers || canViewReports;
      case '/orders/order-list':
        return canViewTasks;
      case '/tasks-list':
        return hasCap('pages.tasks_list');
      case '/orders/order-management':
        return hasCap('pages.order_management');
      case '/orders/pos-sync':
        return hasCap('pages.pos_sync');
      case '/logistics-requests':
        return hasCap('logistics_requests.view') || hasCap('logistics_requests.manage') || canManageSettings;
      case '/route-management':
        return hasCap('routes.view') || hasCap('routes.manage') || hasCap('routes.all');
      case '/fleet/vehicles':
        // RM-F: capability gate AND the pilot feature flag.
        return (
          isVehicleMasterEnabled((currentTenant as any)?.feature_flags) &&
          (hasCap('fleet.vehicles.view') ||
            hasCap('fleet.vehicles.manage') ||
            hasCap('routes.view') ||
            hasCap('routes.manage') ||
            hasCap('routes.all'))
        );
      case '/drivers-management':
        return canManageDrivers;
      case '/reports':
        return canViewReports;
      case '/map':
      case '/track':
        return canViewTasks || canManageDrivers;
      default:
        return false;
    }
  };
  
  // Hide a nav route only when its module is explicitly disabled.
  const hasModuleVisibility = (url: string): boolean => {
    if (modulesLoading) return true;
    const moduleKey = getModuleForPath(url);
    if (!moduleKey) return true;
    return moduleCapabilities[moduleKey] !== false;
  };

  const hasNavAccess = (item: NavigationItem): boolean =>
    hasItemAccess(item) && hasModuleVisibility(item.url);

  // Recursive so nested groups (Logistics → Fleet → Vehicles) are filtered too.
  const hasEntryAccess = (entry: NavigationEntry): boolean =>
    isGroupedItem(entry) ? entry.children.some(hasEntryAccess) : hasNavAccess(entry);

  const filterEntries = (entries: NavigationEntry[]): NavigationEntry[] =>
    entries
      .filter(hasEntryAccess)
      .map(entry =>
        isGroupedItem(entry) ? { ...entry, children: filterEntries(entry.children) } : entry,
      );

  // Filter navigation items based on user capabilities
  const filteredNavigationItems = filterEntries(navigationConfig.navigation);

  const isActive = (path: string) => currentPath === path;
  
  // Check if any (nested) child of a group is active
  const isGroupActive = (group: NavigationItemGroup): boolean =>
    group.children.some(child =>
      isGroupedItem(child) ? isGroupActive(child) : isActive(child.url),
    );

  const getNavClass = (path: string) => {
    const base = "flex items-center gap-3 w-full text-left transition-colors";
    return isActive(path) 
      ? `${base} bg-sidebar-accent text-sidebar-accent-foreground font-medium`
      : `${base} hover:bg-sidebar-accent/50 text-sidebar-foreground`;
  };

  const shouldShowText = open || openMobile;

  const handleMouseEnter = () => {
    if (!isMobile && !open) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setOpen(true);
      }, 200);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (!isMobile && open && !openMobile) {
      hoverTimeoutRef.current = setTimeout(() => {
        setOpen(false);
      }, 300);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Sidebar 
      collapsible="icon" 
      className="border-r border-sidebar-border print:hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold"
            style={{ backgroundColor: currentTenant?.brand_primary || 'hsl(var(--primary))' }}
          >
            <Building2 className="h-4 w-4" />
          </div>
          {shouldShowText && (
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-sm text-sidebar-foreground truncate">
                {navigationConfig.appName}
              </h2>
              <Badge variant="secondary" className="text-xs mt-1">
                {currentTenant?.name}
              </Badge>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-medium text-sidebar-foreground/60 uppercase tracking-wider">
            Navigation
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredNavigationItems.map((item) => {
                if (isGroupedItem(item)) {
                  const groupIsActive = isGroupActive(item);
                  return (
                    <Collapsible
                      key={item.title}
                      defaultOpen={groupIsActive}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton className={`flex items-center gap-3 w-full text-left transition-colors ${groupIsActive ? 'text-sidebar-accent-foreground font-medium' : 'hover:bg-sidebar-accent/50 text-sidebar-foreground'}`}>
                            <item.icon className="h-4 w-4 flex-shrink-0" />
                            {shouldShowText && (
                              <>
                                <span className="flex-1">{item.title}</span>
                                <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                              </>
                            )}
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        {shouldShowText && (
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {item.children.map((child) =>
                                isGroupedItem(child) ? (
                                  // Nested group (Logistics → Fleet → Vehicles)
                                  <SidebarMenuSubItem key={child.title}>
                                    <div className="flex items-center gap-2 px-2 py-1 text-xs font-medium uppercase tracking-wide text-sidebar-foreground/60">
                                      <child.icon className="h-3.5 w-3.5 flex-shrink-0" />
                                      <span>{child.title}</span>
                                    </div>
                                    <SidebarMenuSub className="ml-2">
                                      {child.children.map((leaf) =>
                                        isGroupedItem(leaf) ? null : (
                                          <SidebarMenuSubItem key={leaf.title}>
                                            <SidebarMenuSubButton asChild>
                                              <Link to={leaf.url} className={getNavClass(leaf.url)}>
                                                <leaf.icon className="h-3.5 w-3.5 flex-shrink-0" />
                                                <span className="flex-1">{leaf.title}</span>
                                                {isActive(leaf.url) && (
                                                  <ChevronRight className="h-3 w-3 text-sidebar-accent-foreground/60" />
                                                )}
                                              </Link>
                                            </SidebarMenuSubButton>
                                          </SidebarMenuSubItem>
                                        ),
                                      )}
                                    </SidebarMenuSub>
                                  </SidebarMenuSubItem>
                                ) : (
                                  <SidebarMenuSubItem key={child.title}>
                                    <SidebarMenuSubButton asChild>
                                      <Link
                                        to={child.url}
                                        className={getNavClass(child.url)}
                                      >
                                        <child.icon className="h-3.5 w-3.5 flex-shrink-0" />
                                        <span className="flex-1">{child.title}</span>
                                        {isActive(child.url) && (
                                          <ChevronRight className="h-3 w-3 text-sidebar-accent-foreground/60" />
                                        )}
                                      </Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                ),
                              )}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        )}
                      </SidebarMenuItem>
                    </Collapsible>
                  );
                }
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <Link 
                        to={item.url}
                        className={getNavClass(item.url)}
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        {shouldShowText && (
                          <>
                            <span className="flex-1">{item.title}</span>
                            {isActive(item.url) && (
                              <ChevronRight className="h-3 w-3 text-sidebar-accent-foreground/60" />
                            )}
                          </>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        
        {/* Admin section for users with settings.manage capability */}
        {canManageSettings && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs font-medium text-sidebar-foreground/60 uppercase tracking-wider">
              Administration
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminNavigationItems.map((item) => {
                  if (isGroupedItem(item)) {
                    // Admin navigation is flat (leaf links only).
                    const adminChildren = item.children.filter(
                      (child): child is NavigationItem => !isGroupedItem(child),
                    );
                    const adminGroupActive = adminChildren.some(child => isActive(child.url));
                    return (
                      <Collapsible
                        key={item.title}
                        defaultOpen={adminGroupActive}
                        className="group/collapsible"
                      >
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton className={`flex items-center gap-3 w-full text-left transition-colors ${adminGroupActive ? 'text-sidebar-accent-foreground font-medium' : 'hover:bg-sidebar-accent/50 text-sidebar-foreground'}`}>
                              <item.icon className="h-4 w-4 flex-shrink-0" />
                              {shouldShowText && (
                                <>
                                  <span className="flex-1">{item.title}</span>
                                  <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                                </>
                              )}
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          {shouldShowText && (
                            <CollapsibleContent>
                              <SidebarMenuSub>
                                {adminChildren.map((child) => (
                                  <SidebarMenuSubItem key={child.title}>
                                    <SidebarMenuSubButton asChild>
                                      <Link
                                        to={child.url}
                                        className={getNavClass(child.url)}
                                      >
                                        <child.icon className="h-3.5 w-3.5 flex-shrink-0" />
                                        <span className="flex-1">{child.title}</span>
                                        {isActive(child.url) && (
                                          <ChevronRight className="h-3 w-3 text-sidebar-accent-foreground/60" />
                                        )}
                                      </Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                ))}
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          )}
                        </SidebarMenuItem>
                      </Collapsible>
                    );
                  }
                  
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <Link 
                          to={item.url}
                          className={getNavClass(item.url)}
                        >
                          <item.icon className="h-4 w-4 flex-shrink-0" />
                          {shouldShowText && (
                            <>
                              <span className="flex-1">{item.title}</span>
                              {isActive(item.url) && (
                                <ChevronRight className="h-3 w-3 text-sidebar-accent-foreground/60" />
                              )}
                            </>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4">
        <div className="space-y-2">
          {shouldShowText && (
            <div className="flex items-center gap-3 text-sm text-sidebar-foreground/80">
              <div className="w-6 h-6 rounded-full bg-sidebar-accent flex items-center justify-center text-xs font-medium">
                {user?.email?.charAt(0).toUpperCase()}
              </div>
              <span className="truncate flex-1">{user?.email}</span>
            </div>
          )}

          <Button
            asChild
            variant="ghost"
            size="sm"
            className={`w-full justify-start text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent ${isActive('/account') ? 'bg-sidebar-accent text-sidebar-accent-foreground' : ''}`}
          >
            <Link to="/account">
              <UserCog className="h-4 w-4 flex-shrink-0" />
              {shouldShowText && <span className="ml-2">Account settings</span>}
            </Link>
          </Button>

          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleSignOut}
            className="w-full justify-start text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            {shouldShowText && <span className="ml-2">Sign Out</span>}
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
